import React, { useState, useEffect, useRef, useCallback } from "react";

export interface ClusterComboboxProps {
  value: string;
  onChange: (value: string) => void;
  options: string[];
  placeholder?: string;
  disabled?: boolean;
  isLoading?: boolean;
  className?: string;
  style?: React.CSSProperties;
}

const ClusterCombobox: React.FC<ClusterComboboxProps> = ({
  value,
  onChange,
  options,
  placeholder = "Select or type project cluster...",
  disabled = false,
  isLoading = false,
  className = "",
  style,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [inputValue, setInputValue] = useState(value);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Sync external value changes
  useEffect(() => {
    setInputValue(value);
  }, [value]);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const filteredOptions = inputValue.trim()
    ? options.filter((opt) =>
        opt.toLowerCase().includes(inputValue.toLowerCase())
      )
    : options;

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const val = e.target.value;
      setInputValue(val);
      onChange(val);
      if (!isOpen) setIsOpen(true);
    },
    [onChange, isOpen]
  );

  const handleSelect = useCallback(
    (optionName: string) => {
      setInputValue(optionName);
      onChange(optionName);
      setIsOpen(false);
      inputRef.current?.blur();
    },
    [onChange]
  );

  const handleFocus = () => {
    setIsOpen(true);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Escape") {
      setIsOpen(false);
      inputRef.current?.blur();
    }
  };

  return (
    <div
      ref={containerRef}
      className={`position-relative ${className}`}
      style={style}
    >
      <input
        ref={inputRef}
        type="text"
        className="form-control form-control-compact"
        value={inputValue}
        onChange={handleInputChange}
        onFocus={handleFocus}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        disabled={disabled}
        autoComplete="off"
        style={{
          borderColor: isOpen ? '#2B75DA' : undefined,
          boxShadow: isOpen ? '0 0 0 2px rgba(43, 117, 218, 0.15)' : undefined,
          transition: 'border-color 0.15s ease, box-shadow 0.15s ease',
        }}
      />

      {isOpen && (
        <div
          className="position-absolute w-100 bg-white border rounded-3 shadow-lg mt-1 animate-pop-in"
          style={{
            zIndex: 1050,
            maxHeight: "260px",
            overflowY: "auto",
          }}
        >
          {isLoading ? (
            <div className="px-3 py-2 small text-muted">Loading...</div>
          ) : filteredOptions.length === 0 ? (
            <div className="px-3 py-2 small text-muted">
              {inputValue.trim()
                ? <>Tekan Enter atau klik di luar untuk menggunakan "<strong>{inputValue}</strong>"</>
                : "Tidak ada data cluster"}
            </div>
          ) : (
            filteredOptions.map((opt, idx) => {
              const isSelected = opt.toLowerCase() === inputValue.toLowerCase();
              return (
                <button
                  key={idx}
                  type="button"
                  className={`dropdown-item rounded-0 px-3 py-2 text-start ${
                    isSelected ? "active bg-primary text-white fw-semibold" : "text-dark"
                  }`}
                  style={{
                    fontSize: "0.85rem",
                    height: "auto",
                    minHeight: "fit-content",
                    border: "none",
                    minWidth: "unset",
                  }}
                  onClick={() => handleSelect(opt)}
                >
                  {opt}
                </button>
              );
            })
          )}
        </div>
      )}
    </div>
  );
};

export default ClusterCombobox;
